import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useSession } from "../../lib/session";
import {
  fetchLicenceQueue,
  fetchMyLicenceStatus,
  reviewLicenceCheck,
  submitLicenceCheck,
  type LicenceDueState,
} from "./api";
import { Badge, Button, Card, ErrorBox } from "../../components/ui";
import { fmtDateShort, fmtRelative } from "../../lib/format";

/**
 * The two halves of the six-month DVLA loop, as To Do cards.
 *
 * The database has carried the workflow since the foundation schema;
 * these cards are what makes it happen. A driver is asked for a fresh
 * gov.uk check code when they have never been checked, their last one
 * was rejected, or six months have (nearly) run out. A vehicle admin is
 * shown every code waiting so no submission can sit unreviewed --
 * which is exactly what the first real code did, for two weeks, before
 * these cards existed.
 *
 * Both render nothing when there is nothing to do. A pending code is
 * the reviewer's item, not the driver's, so it appears in one To Do,
 * not two.
 */

/** The driver owes an action in these states and no others. */
export function licenceActionNeeded(state: LicenceDueState | undefined): boolean {
  return state === "never" || state === "rejected" || state === "overdue" || state === "due_soon";
}

const ASK: Record<string, { badge: string; tone: "bad" | "warn"; line: string }> = {
  never: { badge: "Never checked", tone: "bad", line: "No DVLA licence check on record yet." },
  rejected: { badge: "Rejected", tone: "bad", line: "Your last check was rejected — submit a fresh code." },
  overdue: { badge: "Overdue", tone: "bad", line: "Your six-monthly check has lapsed." },
  due_soon: { badge: "Due soon", tone: "warn", line: "Your six-monthly check is nearly due." },
};

export function MyLicencePrompt() {
  const { can } = useSession();
  const qc = useQueryClient();
  const [code, setCode] = useState("");

  const q = useQuery({
    queryKey: ["my-licence-status"],
    queryFn: fetchMyLicenceStatus,
    staleTime: 60_000,
    enabled: can("can_view_vehicles"),
  });

  const submit = useMutation({
    mutationFn: () => submitLicenceCheck(code),
    onSuccess: () => {
      setCode("");
      void qc.invalidateQueries({ queryKey: ["my-licence-status"] });
      void qc.invalidateQueries({ queryKey: ["licence-queue"] });
    },
  });

  if (!can("can_view_vehicles") || q.isLoading || q.error) return null;
  const s = q.data;
  if (!s || !licenceActionNeeded(s.due_state)) return null;
  const ask = ASK[s.due_state]!;

  return (
    <Card title="DVLA licence check">
      <div className="check-due-row">
        <span>
          <Badge tone={ask.tone}>{ask.badge}</Badge>
          <span className="attention-item-detail">
            {" "}
            {ask.line}
            {s.next_due_date && ` Due ${fmtDateShort(s.next_due_date)}.`}
          </span>
        </span>
      </div>
      <p className="hint" style={{ margin: "6px 0" }}>
        Get a one-time code at gov.uk/view-driving-licence (Share your licence information) and paste it here.
      </p>
      <form
        className="row-tight"
        onSubmit={(e) => {
          e.preventDefault();
          if (code.trim()) submit.mutate();
        }}
      >
        <input
          value={code}
          onChange={(e) => setCode(e.target.value)}
          placeholder="Check code, e.g. Ab cd ef Gh"
          style={{ flex: 1, minWidth: 160 }}
        />
        <Button size="sm" variant="primary" type="submit" disabled={!code.trim() || submit.isPending}>
          Submit
        </Button>
      </form>
      <ErrorBox error={submit.error} />
    </Card>
  );
}

export function LicenceReviewQueue() {
  const { can } = useSession();
  const qc = useQueryClient();

  const q = useQuery({
    queryKey: ["licence-queue"],
    queryFn: fetchLicenceQueue,
    staleTime: 60_000,
    enabled: can("can_manage_vehicles"),
  });

  const review = useMutation({
    mutationFn: ({ id, approve }: { id: number; approve: boolean }) => reviewLicenceCheck(id, approve),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["licence-queue"] });
      void qc.invalidateQueries({ queryKey: ["my-licence-status"] });
      void qc.invalidateQueries({ queryKey: ["people-licence-statuses"] });
    },
  });

  if (!can("can_manage_vehicles") || q.isLoading || q.error) return null;
  const rows = q.data ?? [];
  if (rows.length === 0) return null;

  return (
    <Card
      title={
        rows.length === 1 ? "A DVLA check code is waiting for review" : `${rows.length} DVLA check codes are waiting for review`
      }
    >
      <p className="hint" style={{ marginTop: 0 }}>
        Look each code up at gov.uk/check-driving-licence-information, then approve or reject. Approval logs the check and sets
        the next one due in six months.
      </p>
      <div className="attention-list">
        {rows.map((r) => (
          <div key={r.id} className="check-due-row">
            <span>
              <strong>{r.full_name}</strong>
              <span className="attention-item-detail">
                {" — "}
                <code>{r.check_code}</code>, submitted {fmtRelative(r.submitted_at)}
              </span>
            </span>
            <span className="row-tight">
              <Button
                size="sm"
                variant="primary"
                disabled={review.isPending}
                onClick={() => review.mutate({ id: r.id, approve: true })}
              >
                Approve
              </Button>
              <Button size="sm" disabled={review.isPending} onClick={() => review.mutate({ id: r.id, approve: false })}>
                Reject
              </Button>
            </span>
          </div>
        ))}
      </div>
      <ErrorBox error={review.error} />
    </Card>
  );
}
